import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useId, useState } from 'react';
import { differenceInDays, subDays } from 'date-fns';
import { useTranslation } from '@crehana/i18n';
import { Tab } from "../../Tab";
import { Tabs } from "../../Tabs";
import { Calendar } from "../Calendar";
import { Popper, PopperContent, PopperTrigger } from "../Popper";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select";
import { cn } from "../utils";
import { DateInput } from "./components/DateInput";
import locales from "./i18n";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var AVAILABLE_PERIOD_LENGTHS = [7, 15, 30, 60, 90, 180, 365];
var setToEndOfDay = function setToEndOfDay(date) {
  date.setHours(23, 59, 59, 999);
  return date;
};
export var tabs = [{
  label: 'DATE_PICKER_LAST_TAB',
  value: 'last'
}, {
  label: 'DATE_PICKER_SINGLE_TAB',
  value: 'single'
}, {
  label: 'DATE_PICKER_RANGE_TAB',
  value: 'range'
}];
export var DatePickerWithTabs = function DatePickerWithTabs(_ref) {
  var _AVAILABLE_PERIOD_LEN;
  var value = _ref.value,
    defaultTab = _ref.defaultTab,
    _ref$enabledTabs = _ref.enabledTabs,
    enabledTabs = _ref$enabledTabs === void 0 ? ['last', 'single', 'range'] : _ref$enabledTabs,
    onChange = _ref.onChange,
    className = _ref.className,
    disabled = _ref.disabled;
  var _useTranslation = useTranslation(),
    languageCode = _useTranslation.languageCode;
  if (defaultTab && !enabledTabs.includes(defaultTab)) {
    throw new Error('defaultTab must be one of the enabledTabs');
  }
  var defaultTabIndex = tabs.findIndex(function (_ref2) {
    var value = _ref2.value;
    return value === defaultTab;
  });
  var fallbackTabIndex = tabs.findIndex(function (_ref3) {
    var value = _ref3.value;
    return value === enabledTabs[0];
  });
  var _useState = useState(defaultTabIndex === -1 ? fallbackTabIndex : defaultTabIndex),
    _useState2 = _slicedToArray(_useState, 2),
    selectedTab = _useState2[0],
    setSelectedTab = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedDateLabel = _useState4[0],
    setSelectedDateLabel = _useState4[1];
  var id = useId();
  var isPeriodError = (value === null || value === void 0 ? void 0 : value.from) && !(value !== null && value !== void 0 && value.to);
  var onTabChange = function onTabChange(value) {
    setSelectedTab(value);
    setSelectedDateLabel(undefined);
    onChange === null || onChange === void 0 ? void 0 : onChange(undefined);
  };
  var handleSetPeriod = function handleSetPeriod(selected) {
    setSelectedDateLabel(selected);
    if (!selected) return;
    var dates = {};
    if (typeof selected === 'string') {
      dates = {
        from: subDays(new Date(), Number(selected)),
        to: setToEndOfDay(new Date())
      };
    } else if (selected instanceof Date) {
      dates = {
        from: selected,
        to: setToEndOfDay(selected)
      };
    } else {
      var selectedDate = selected;
      dates = {
        from: selectedDate.from,
        to: selectedDate.to ? setToEndOfDay(selectedDate.to) : undefined
      };
    }
    onChange === null || onChange === void 0 ? void 0 : onChange({
      tab: tabs[selectedTab].value,
      dates: dates
    });
  };
  var lastPeriods = AVAILABLE_PERIOD_LENGTHS.map(function (days) {
    return {
      label: locales[languageCode].DATE_PICKER_LAST_LABEL.replace('{{count}}', days.toString()),
      value: days.toString()
    };
  });
  var selectedPeriodMode = tabs[selectedTab].value;
  var handleMaskPeriod = selectedPeriodMode === 'last' ? function (selectedValues) {
    return locales[languageCode].DATE_PICKER_LAST_LABEL.replace('{{count}}', selectedValues.toString());
  } : undefined;
  var defaultLastPeriodValue = value !== null && value !== void 0 && value.from ? (_AVAILABLE_PERIOD_LEN = AVAILABLE_PERIOD_LENGTHS.find(function (days) {
    return days === differenceInDays(new Date(), value.from);
  })) === null || _AVAILABLE_PERIOD_LEN === void 0 ? void 0 : _AVAILABLE_PERIOD_LEN.toString() : lastPeriods[0].value;
  var tabContent = {
    last: /*#__PURE__*/_jsxs(Select, {
      disabled: disabled,
      onValueChange: handleSetPeriod,
      defaultValue: defaultLastPeriodValue,
      children: [/*#__PURE__*/_jsx(SelectTrigger, {
        className: "tw-w-28 tw-mb-1",
        children: /*#__PURE__*/_jsx(SelectValue, {
          placeholder: lastPeriods[0].label
        })
      }), /*#__PURE__*/_jsx(SelectContent, {
        className: "tw-w-auto",
        children: lastPeriods.map(function (_ref4) {
          var label = _ref4.label,
            value = _ref4.value;
          return /*#__PURE__*/_jsx(SelectItem, {
            value: value,
            children: label
          }, label);
        })
      })]
    }),
    single: /*#__PURE__*/_jsx(Calendar, {
      disabled: disabled,
      id: "".concat(id, "-date-picker-single"),
      mode: "single",
      selected: value === null || value === void 0 ? void 0 : value.from,
      onSelect: handleSetPeriod,
      defaultMonth: value === null || value === void 0 ? void 0 : value.from,
      initialFocus: true,
      className: "tw-shadow-none tw-p-0 tw-pb-1"
    }),
    range: /*#__PURE__*/_jsx(Calendar, {
      disabled: disabled,
      id: "".concat(id, "-date-picker-range"),
      mode: "range",
      selected: value,
      defaultMonth: value === null || value === void 0 ? void 0 : value.to,
      onSelect: handleSetPeriod,
      initialFocus: true,
      className: "tw-shadow-none tw-p-0 tw-pb-1"
    })
  };
  return /*#__PURE__*/_jsxs(Popper, {
    children: [/*#__PURE__*/_jsx(PopperTrigger, {
      children: /*#__PURE__*/_jsx(DateInput, {
        disabled: disabled,
        mode: selectedPeriodMode !== 'last' ? selectedPeriodMode : 'single',
        selected: selectedDateLabel || value,
        onMaskValue: handleMaskPeriod,
        placeholder: locales[languageCode].DATE_PICKER_PLACEHOLDER,
        className: cn(className || 'sm:tw-w-[32rem] tw-w-full', isPeriodError && 'tw-border-alert-400 tw-border-solid')
      })
    }), /*#__PURE__*/_jsx(PopperContent, {
      className: "tw-w-auto",
      removePadding: true,
      children: /*#__PURE__*/_jsxs("div", {
        className: "tw-flex tw-flex-col tw-gap-1 tw-rounded tw-p-1 tw-pt-2 tw-bg-neutral-light-100 dark:tw-bg-elevation-dark-03",
        children: [/*#__PURE__*/_jsx(Tabs, {
          value: selectedTab,
          onTabChange: onTabChange,
          className: "tw-flex tw-justify-center tw-bg-transparent dark:tw-bg-transparent",
          children: tabs.map(function (_ref5) {
            var label = _ref5.label,
              value = _ref5.value;
            return /*#__PURE__*/_jsx(Tab, {
              label: locales[languageCode][label],
              className: cn(!enabledTabs.includes(value) && 'tw-hidden')
            }, label);
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "tw-w-full tw-h-full tw-px-2 tw-pt-1",
          children: tabContent[selectedPeriodMode]
        })]
      })
    })]
  });
};